import { InputV2 } from "../../Reusable/InputV2/InputV2";
import { Formik } from "formik";
import { Box, SimpleGrid, FormLabel, Button } from "@chakra-ui/react";
import { MetricModalFormWrapper } from "./Styles";
import { SelectV2 } from "../../Reusable";
import RadioButtonGroup from "../RadioButtonGroup/RadioButtonGroup";
import { BlackFundMetrics, Metric } from "../../../redux/reducers/types";
import { useAddBlackFundMetricMutation, useDeleteBlackFundMetricMutation, useUpdateBlackFundMetricMutation } from "../../../redux/reducers/apiSlice";
// =======================
//   VALIDATION SCHEMA
// =======================
import * as yup from "yup";
import { toast } from "react-hot-toast";
import { useState } from "react";
import { Modal } from "./Modal";

export const blackFundImpactSchema = yup.object().shape({
  new_funding_secured: yup.number(),
  increase_in_fundraising_confidence: yup.string(),
  full_staff: yup.number().integer(),
  part_time_staff: yup.number().integer(),
  active_volunteers: yup.number().integer(),
  organizational_sustainability: yup.string(),
});


const generateFundraisingConfidenceOptions = () => {

    return [{value: 1, label: "1 = Not confident at all"}, { value: 2, label: "2 = Slightly confident"}, { value: 3, label: "3 = Somewhat confident"}, { value: 4, label: "4 = Confident"}, { value: 5, label: "5 = Very confident"}]
}

const EditBlackFundImpactMetricModal = ({ metric, isOpen, onClose }: { metric?: BlackFundMetrics; onClose: () => void; isOpen: boolean; }) => {

  const [updateBlackFundMetric, { isLoading }] = useUpdateBlackFundMetricMutation();
  const editMode = !!metric;


  return (
    <Formik
      validationSchema={blackFundImpactSchema}
      initialValues={{
          new_funding_secured: parseFloat(metric.newFundingSecured) || 0, 
          increase_in_fundraising_confidence: metric.increaseInFundraisingConfidence || "1",
          full_staff: metric.fullStaff || 0,
          part_time_staff: metric.partTimeStaff || 0,
          active_volunteers: metric.activeVolunteers || 0,
          organizational_sustainability: metric.organizationalSustainability || ""
      }}
      onSubmit={async (data) => {
        try {
          await updateBlackFundMetric({ data, orgId: metric.organization, goalId: metric.goal, metricId: metric.id });
          toast.success("Your black fund metric was successfully created");
        } catch(e){
          toast.error("Error saving black fund metric. Try again later!");
        }
      }}
    >
      {(formik) => {

        return (
          <Modal
          
            isOpen={isOpen}
            onClose={onClose}
            primaryButtonText={"Edit metric"}
            secondaryButtonText={"Cancel"}
            modalTitle={`Edit Black Fund metric`}
            onPrimaryButtonClicked={formik.handleSubmit}
            primaryLoading={isLoading}
            disablePrimaryButton={isLoading}
          >


          <Box
          >
            <MetricModalFormWrapper>

              {/* ================= 1. FUNDING GROWTH ================= */}
              <Box mb={6} style={{ position: "relative", zIndex: 120 }}>
                <h2 style={{ fontWeight: "bold", marginBottom: 12 }}>
                  1. Funding Growth
                </h2>

                <InputV2
                  title="$ of new funding secured since receiving The Black Fund"
                  placeholder="Enter amount"
                  name="new_funding_secured"
                  value={formik.values.new_funding_secured}
                  restInput={{
                    onChange: (e) => {
                      const cleaned = e.target.value.replace(/[^0-9]/g, "");
                      formik.setFieldValue(
                        "new_funding_secured",
                        cleaned
                      );
                    },
                  }}
                />
              </Box>

              {/* ================= 2. FUNDRAISING CONFIDENCE ================= */}
              <Box mb={6} style={{ position: "relative", zIndex: 119 }}>
                <h2 style={{ fontWeight: "bold", marginBottom: 12 }}>
                  2. Fundraising Confidence
                </h2>

                <FormLabel>% of increase in fundraising confidence (1–5 scale)</FormLabel>
                <SelectV2
                  name="increase_in_fundraising_confidence"
                  placeholder="Select a confidence level"
                  options={generateFundraisingConfidenceOptions()}
                  value={formik.values.increase_in_fundraising_confidence}
                  onChange={(opt) =>
                    formik.setFieldValue(
                      "increase_in_fundraising_confidence",
                      opt.value
                    )
                  }
                  style={{ marginBottom: 20, position: "relative", zIndex: 119 }}
                />
              </Box>

              {/* ================= 3. ORGANIZATIONAL CAPACITY ================= */}
              <Box mb={6} style={{ position: "relative", zIndex: 118 }}>
                <h2 style={{ fontWeight: "bold", marginBottom: 12 }}>
                  3. Organizational Capacity
                </h2>

                <SimpleGrid columns={[1, 2]} spacingX="44px">
                  <InputV2
                    title="# of Full-time staff"
                    name="full_staff"
                    placeholder="0"
                    value={
                      formik.values.full_staff
                    }
                    restInput={{
                      onChange: (e) =>
                        formik.setFieldValue(
                          "full_staff",
                          e.target.value.replace(/[^0-9]/g, "")
                        ),
                    }}
                  />

                  <InputV2
                    title="# of Part-time staff"
                    name="part_time_staff"
                    placeholder="0"
                    value={formik.values.part_time_staff}
                    restInput={{
                      onChange: (e) =>
                        formik.setFieldValue(
                          "part_time_staff",
                          e.target.value.replace(/[^0-9]/g, "")
                        ),
                    }}
                  />
                </SimpleGrid>

                <Box mt={4}>
                  <InputV2
                    title="Active volunteers"
                    name="active_volunteers"
                    placeholder="0"
                    value={formik.values.active_volunteers}
                    restInput={{
                      onChange: (e) =>
                        formik.setFieldValue(
                          "active_volunteers",
                          e.target.value.replace(/[^0-9]/g, "")
                        ),
                    }}
                  />
                </Box>
              </Box>

              {/* ================= 4. ORGANIZATIONAL SUSTAINABILITY ================= */}
              <Box mb={6} style={{ position: "relative", zIndex: 117 }}>
                <h2 style={{ fontWeight: "bold", marginBottom: 12 }}>
                  4. Organizational Sustainability
                </h2>

                <FormLabel>
                  I feel positive about my organization’s sustainability since
                  receiving funding from The Black Fund.
                </FormLabel>

                <RadioButtonGroup
                  formik={formik}
                  name="organizational_sustainability"
                  value={formik.values.organizational_sustainability}
                  onChange={(val) =>
                    formik.setFieldValue(
                      "organizational_sustainability",
                      val
                    )
                  }
                  options={[
                    { label: "Yes", value: "YES" },
                    { label: "No", value: "NO" },
                    { label: "Unsure", value: "UNSURE" },
                  ]}
                />
              </Box>
            </MetricModalFormWrapper>
          </Box>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default EditBlackFundImpactMetricModal;
